import React from "react";
import { useNavigate } from "react-router-dom";

import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { Formik } from "formik";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import api from "../../services/BackendServices";
import { setUserProfile } from "../../store/userProfile";

const AuthLogin = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const [error, setError] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const validations = Yup.object().shape({
    email: Yup.string()
      .email("Tiene que ingresar un mail correcto")
      .max(255)
      .required("El mail es requerido"),
    password: Yup.string().max(255).required("Ingrese la contraseña"),
  });

  const handleRegister = () => {
    navigate("/register");
  };

  const login = async (
    values: any,
    { setErrors, setStatus, setSubmitting }: any
  ) => {
    setLoading(true);
    setError("");
    try {
      api.SecurityServices.login({
        email: values.email,
        password: values.password,
      })
        .then((response) => {
          // Guardar id, token o datos de usuario
          dispatch(
            setUserProfile({
              token: response.data.expressToken,
              expressToken: response.data.expressToken,
              realm: response.data?.realm,
              maxFileSize: 5,
            })
          );
          // if (userData.hasPlan) {
          //   // Si tiene un plan, redirigir al chat
          //   navigate("/chat");
          // } else {
          //   // Si no tiene un plan, redirigir a la página de selección de planes
          //   navigate("/select-plan");
          // }
          navigate("/chat");
        })
        .catch(({ response }) => {
          setError(response.data.error.message);
          setLoading(false);
        });

      setStatus({ success: false });
      setSubmitting(false);
    } catch (err: any) {
      setLoading(false);
      setStatus({ success: false });
      setErrors({ submit: err.message });
      setSubmitting(false);
    }
  };

  return (
    <>
      {error && (
        <Alert style={{ marginBottom: "1rem" }} severity="error">
          {error}
        </Alert>
      )}
      <Formik
        initialValues={{
          email: "",
          password: "",
          submit: null,
        }}
        validationSchema={validations}
        onSubmit={login}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} style={{ paddingLeft: 0 }}>
                <Stack spacing={1}>
                  <InputLabel style={{ color: "#ffff" }} htmlFor="email-login">
                    Email{" "}
                  </InputLabel>
                  <OutlinedInput
                    id="email-login"
                    type="email"
                    style={{
                      color: "#f1f5f9",
                      backgroundColor: "transparent",
                    }}
                    value={values.email}
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="mail@mail.com"
                    fullWidth
                    error={Boolean(touched.email && errors.email)}
                    inputProps={{
                      style: { color: "#f1f5f9" }, // asegura que el texto se vea
                    }}
                    sx={{
                      // Color del texto y placeholder
                      "& input::placeholder": {
                        color: "#4b5563",
                        opacity: 1,
                      },
                      // Borde normal
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4b5563",
                      },
                      // Borde hover
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#64748b",
                      },
                      // Borde cuando el input está enfocado
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                      // Estilo para el autofill (Chrome, Safari)
                      "& input:-webkit-autofill": {
                        WebkitBoxShadow: "0 0 0 1000px #1a1c1e inset",
                        WebkitTextFillColor: "#f1f5f9",
                        transition: "background-color 5000s ease-in-out 0s",
                      },
                    }}
                  />

                  {touched.email && errors.email && (
                    <FormHelperText
                      error
                      id="standard-weight-helper-text-email-login"
                    >
                      {errors.email.toString()}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} style={{ paddingLeft: 0 }}>
                <Stack spacing={1}>
                  <InputLabel
                    style={{ color: "#ffff" }}
                    htmlFor="password-login"
                  >
                    Contraseña
                  </InputLabel>
                  <OutlinedInput
                    style={{ color: "#f1f5f9", backgroundColor: "transparent" }}
                    fullWidth
                    error={Boolean(touched.password && errors.password)}
                    id="password-login"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {showPassword ? (
                            <EyeOutlined style={{ color: "#4b5563" }} />
                          ) : (
                            <EyeInvisibleOutlined
                              style={{ color: "#4b5563" }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    placeholder="Ingresar la contraseña"
                    inputProps={{
                      style: { color: "#f1f5f9" },
                    }}
                    sx={{
                      "& input::placeholder": {
                        color: "#4b5563",
                        opacity: 1,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#4b5563",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#64748b",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                      "& input:-webkit-autofill": {
                        WebkitBoxShadow: "0 0 0 1000px #1a1c1e inset",
                        WebkitTextFillColor: "#f1f5f9",
                        transition: "background-color 5000s ease-in-out 0s",
                      },
                    }}
                  />

                  {touched.password && errors.password && (
                    <FormHelperText
                      error
                      id="standard-weight-helper-text-password-login"
                    >
                      {errors.password.toString()}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>
                    {errors.submit.toString()}
                  </FormHelperText>
                </Grid>
              )}
              <Grid item xs={12} style={{ paddingLeft: 0 }}>
                <Button
                  disableElevation
                  disabled={loading}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="outlined"
                  color="primary"
                  style={{
                    background: "#3b82f6",
                    border: "none",
                    padding: "10px",
                    color: "#f1f5f9",
                  }}
                >
                  INGRESAR
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ mt: -1 }}
                style={{
                  paddingLeft: 0,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <p className="login-text">
                  ¿Todavía no tienes cuenta?
                  <a onClick={handleRegister}> Regístrate</a>
                </p>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </>
  );
};

export default AuthLogin;
